/**
 * Where the SPA learns which stack it belongs to.
 *
 * Two sources, in this order:
 *
 * 1. `VITE_*` environment variables, baked in at build time — **only in a
 *    development build**. This is how the dev server and the mock mode run.
 * 2. `config.json`, fetched at boot. This is how a deployed build is
 *    configured: the API endpoint and the Cognito ids are CloudFormation
 *    outputs, so they do not exist when the bundle is built. The CDK stack
 *    writes the file beside `index.html` at deploy time
 *    (`deployWebAssets` in `packages/infra`).
 *
 * A production build ignores the environment entirely, which is not a
 * convenience but a guard. `vite build` reads `.env.local`, so a bundle built
 * on a laptop used to carry that developer's values — and a deployed site
 * then signed people in with a placeholder client id while `config.json` sat
 * beside it, correct and unread. Ignoring the environment in production makes
 * the deployed bundle environment-agnostic by construction rather than by
 * remembering to move a file.
 */
export interface RuntimeConfig {
  readonly apiBaseUrl: string;
  /** All three or none: `createAuth` falls back without a complete set. */
  readonly cognitoDomain?: string;
  readonly cognitoClientId?: string;
  readonly cognitoScopes?: string;
}

export interface RuntimeConfigEnv {
  /** Vite sets this false for `vite build`. */
  readonly DEV?: boolean;
  /**
   * Vite types `import.meta.env` with an index signature, so the named keys
   * below need one too for it to be assignable here.
   */
  readonly [key: string]: unknown;
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_COGNITO_DOMAIN?: string;
  readonly VITE_COGNITO_CLIENT_ID?: string;
  readonly VITE_COGNITO_SCOPES?: string;
}

/**
 * The build-time configuration, or undefined when there is none to use.
 *
 * Nothing is read outside a development build, whatever the variables say.
 */
export function configFromEnv(
  env: RuntimeConfigEnv,
): RuntimeConfig | undefined {
  if (!env.DEV || !env.VITE_API_BASE_URL) {
    return undefined;
  }

  return {
    apiBaseUrl: env.VITE_API_BASE_URL,
    cognitoDomain: env.VITE_COGNITO_DOMAIN || undefined,
    cognitoClientId: env.VITE_COGNITO_CLIENT_ID || undefined,
    cognitoScopes: env.VITE_COGNITO_SCOPES || undefined,
  };
}

/**
 * Validates what `config.json` returned. It is fetched, so it is untrusted
 * input in the ordinary sense — a stale or truncated file must say so here
 * rather than surface later as a sign-in redirect containing `undefined`.
 */
export function parseRuntimeConfig(value: unknown): RuntimeConfig {
  if (typeof value !== "object" || value === null) {
    throw new Error("config.json is not an object");
  }

  const record = value as Record<string, unknown>;
  const apiBaseUrl = record.apiBaseUrl;
  if (typeof apiBaseUrl !== "string" || apiBaseUrl === "") {
    throw new Error("config.json has no apiBaseUrl");
  }

  const optional = (key: string): string | undefined => {
    const each = record[key];
    return typeof each === "string" && each !== "" ? each : undefined;
  };

  return {
    apiBaseUrl,
    cognitoDomain: optional("cognitoDomain"),
    cognitoClientId: optional("cognitoClientId"),
    cognitoScopes: optional("cognitoScopes"),
  };
}

export interface LoadRuntimeConfigDeps {
  readonly env: RuntimeConfigEnv;
  readonly fetchFn: typeof fetch;
  /**
   * Root-absolute on purpose. A path-relative `config.json` resolves against
   * the current route, so booting on `/auth/callback` fetches
   * `/auth/config.json` — which the SPA fallback answers with `index.html` and
   * a 200, and the parse fails with `Unexpected token '<'`. The leading slash
   * keeps it on whichever origin served the app without following the route.
   */
  readonly url?: string;
}

export async function loadRuntimeConfig({
  env,
  fetchFn,
  url = "/config.json",
}: LoadRuntimeConfigDeps): Promise<RuntimeConfig> {
  const fromEnv = configFromEnv(env);
  if (fromEnv) {
    return fromEnv;
  }

  const response = await fetchFn(url);
  if (!response.ok) {
    // The deployment writes this file, so its absence means the upload is
    // incomplete — not something the app can carry on without.
    throw new Error(
      `config.json could not be read (${response.status}); the deployment is incomplete`,
    );
  }

  return parseRuntimeConfig(await response.json());
}
